import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, Calendar, FileText, Settings, LogOut, ChevronRight, CheckSquare, Bell, BarChart3, X, ClipboardList, Repeat, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';

const MainLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [openTabs, setOpenTabs] = useState([]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['Administrador', 'Colaborador'] },
    { name: 'Clientes', href: '/clients', icon: Users, roles: ['Administrador', 'Colaborador'] },
    { name: 'Vencimientos', href: '/deadlines', icon: FileText, roles: ['Administrador', 'Colaborador'] },
    { name: 'Tareas', href: '/tasks', icon: CheckSquare, roles: ['Administrador', 'Colaborador'] },
    { name: 'Calendario', href: '/calendar', icon: Calendar, roles: ['Administrador', 'Colaborador'] },
    { name: 'Seguimiento', href: '/seguimiento', icon: ClipboardList, roles: ['Administrador', 'Colaborador'] },
    { name: 'Conversiones', href: '/conversiones', icon: Repeat, roles: ['Administrador', 'Colaborador'], requiresConversiones: true },
    { name: 'Tributario', href: '/tributario', icon: Landmark, roles: ['Administrador', 'Colaborador'], requiresTributario: true },
    { name: 'Reportes', href: '/reports', icon: BarChart3, roles: ['Administrador'] },
    { name: 'Usuarios', href: '/users', icon: Settings, roles: ['Administrador'] },
    { name: 'Configuracion', href: '/settings', icon: Bell, roles: ['Administrador'] },
  ];

  const filteredNavigation = navigation.filter((item) =>
    item.roles.includes(user?.role) &&
    (!item.requiresConversiones || user?.role === 'Administrador' || user?.acceso_conversiones) &&
    (!item.requiresTributario || user?.role === 'Administrador' || user?.acceso_tributario)
  );

  // Map paths to tab names
  const getTabInfo = useCallback((pathname) => {
    // Handle client detail pages
    if (pathname.startsWith('/clients/')) {
      return { name: 'Detalle Cliente', href: pathname, icon: Users };
    }
    if (pathname.startsWith('/tributario/')) {
      return { name: 'Declaración', href: pathname, icon: Landmark };
    }
    const navItem = navigation.find(n => pathname.startsWith(n.href));
    if (navItem) return { name: navItem.name, href: navItem.href, icon: navItem.icon };
    return { name: 'Página', href: pathname, icon: FileText };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add current route as tab
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/login') return;

    setOpenTabs(prev => {
      const exists = prev.find(t => t.href === path);
      if (exists) return prev;
      const tabInfo = getTabInfo(path);
      return [...prev, tabInfo];
    });
  }, [location.pathname, getTabInfo]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeTab = (e, tabHref) => {
    e.stopPropagation();
    setOpenTabs(prev => {
      const filtered = prev.filter(t => t.href !== tabHref);
      // If closing the active tab, navigate to the last remaining tab or dashboard
      if (location.pathname === tabHref) {
        if (filtered.length > 0) {
          navigate(filtered[filtered.length - 1].href);
        } else {
          navigate('/dashboard');
        }
      }
      return filtered;
    });
  };

  const isActiveTab = (tabHref) => {
    return location.pathname === tabHref;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 bg-white border-r border-slate-200 shadow-sm transition-all duration-300 ease-in-out z-50 ${
          sidebarExpanded ? 'w-64' : 'w-20'
        }`}
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 flex flex-col items-center border-b border-slate-200">
            <img
              src="https://customer-assets.emergentagent.com/job_contador-profesional/artifacts/u6a4cz89_AS%20CONSULTING%20%282%29.png"
              alt="AS CONSULTING"
              className={`${sidebarExpanded ? 'h-24' : 'h-12'} w-auto mb-4 transition-all duration-300`}
            />
            {sidebarExpanded && (
              <>
                <h1 className="text-2xl font-bold text-blue-600 mb-1" style={{ fontFamily: 'Outfit' }}>
                  Firma Contable
                </h1>
                <p className="text-sm text-slate-400 tracking-wider">AS CONSULTING SAS</p>
              </>
            )}
          </div>

          <nav className="flex-1 px-3 py-6 space-y-2">
            {filteredNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-50 text-blue-600 font-medium shadow-sm'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                  } ${
                    !sidebarExpanded ? 'justify-center' : ''
                  }`
                }
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarExpanded && <span className="whitespace-nowrap">{item.name}</span>}
              </NavLink>
            ))}
          </nav>

          {!sidebarExpanded && (
            <div className="p-4 flex justify-center border-t border-slate-200">
              <ChevronRight className="w-4 h-4 text-blue-400 animate-pulse" />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${
        sidebarExpanded ? 'ml-64' : 'ml-20'
      }`}>
        {/* Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-40 shadow-sm">
          <div className="text-sm text-slate-600">Bienvenido, <span className="font-medium text-slate-900">{user?.name}</span></div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 px-3 py-1 bg-slate-100 rounded-full">
              {user?.role}
            </span>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesion
            </Button>
          </div>
        </header>

        {/* Tabs Bar */}
        {openTabs.length > 0 && (
          <div className="bg-slate-100 border-b border-slate-200 px-4 flex items-end overflow-x-auto sticky top-14 z-30" data-testid="tabs-bar">
            {openTabs.map((tab) => {
              const Icon = tab.icon;
              const active = isActiveTab(tab.href);
              return (
                <div
                  key={tab.href}
                  onClick={() => navigate(tab.href)}
                  className={`group flex items-center gap-1.5 px-4 py-2 cursor-pointer border-t-2 transition-all text-xs font-medium rounded-t-md mr-0.5 ${
                    active
                      ? 'bg-white border-t-[#1F3864] text-[#1F3864] shadow-sm'
                      : 'bg-slate-50 border-t-transparent text-slate-500 hover:bg-white hover:text-slate-700'
                  }`}
                  data-testid={`tab-${tab.href.replace(/\//g, '-')}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap max-w-[120px] truncate">{tab.name}</span>
                  <button
                    onClick={(e) => closeTab(e, tab.href)}
                    className={`ml-1 rounded-full p-0.5 transition-all ${
                      active
                        ? 'hover:bg-slate-200 text-slate-400 hover:text-slate-700'
                        : 'opacity-0 group-hover:opacity-100 hover:bg-slate-200 text-slate-400 hover:text-slate-700'
                    }`}
                    data-testid={`tab-close-${tab.href.replace(/\//g, '-')}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
